/**
 * Single place for dashboard / header / profile UI rules.
 * /me can expose available_roles as an array or (legacy) JSON string; localStorage may be stale.
 */

export function normalizeAvailableRoles(user) {
  const ar = user?.available_roles;
  if (!ar) return [];
  if (Array.isArray(ar)) return ar;
  if (typeof ar === 'string') {
    try {
      const p = JSON.parse(ar);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
}

export function isInviteRegistrationShape(user) {
  const rt = String(user?.registration_type || '').toLowerCase();
  return rt === 'invite';
}

/**
 * Subcontractor dashboard access from real relationships on /me: at least one employer link
 * (active or pending). Not derived from available_roles (those flags can be misleading).
 */
export function hasSubcontractorEmployerRelationship(user) {
  return Array.isArray(user?.employers) && user.employers.length > 0;
}

/** Alias: show org/sub tax fields, subcontractor routes, etc. */
export function hasSubcontractorDashboardAccess(user) {
  return hasSubcontractorEmployerRelationship(user);
}

/**
 * Show Employer ⟷ Subcontractor dashboard switch on profile only when both apply:
 * - User works as a subcontractor (≥1 employer relationship or pending request on /me.employers)
 * - User is not limited to the subcontractor-only shell (can use employer dashboard — matches login/shell routing)
 */
export function showEmployerSubcontractorDashboardSwitch(user) {
  if (!user) return false;
  if (!hasSubcontractorEmployerRelationship(user)) return false;
  if (user.use_subcontractor_dashboard_shell === true) return false;
  return true;
}

/** True when the active dashboard context is employer (contractor), not subcontractor-only mode. */
export function isEmployerDashboardRoleActive(user) {
  if (!user) return false;
  const r = String(user.current_role || '').toLowerCase();
  return r === 'contractor' || r === 'both';
}

/** Matches backend use_subcontractor_dashboard_shell intent (sub-only / invite trade shell). */
export function isSubcontractorOnlyShell(user) {
  if (!user) return false;
  if (user.use_subcontractor_dashboard_shell === true) return true;
  if (user.role === 'subcontractor') return true;
  const roles = normalizeAvailableRoles(user);
  if (roles.includes('subcontractor') && !roles.includes('contractor')) return true;
  if (
    isInviteRegistrationShape(user) &&
    user.role === 'subcontractor' &&
    roles.includes('contractor')
  ) {
    return true;
  }
  return false;
}

function norm(v) {
  if (v == null) return '';
  const s = String(v).trim();
  return s || '';
}

/** First name + surname, else full_name (mirrors backend sole-trader label helper). */
export function soleTraderPersonalLabel(user) {
  if (!user) return '';
  const fn = norm(user.first_name);
  const sn = norm(user.surname);
  if (fn || sn) return [fn, sn].filter(Boolean).join(' ');
  return norm(user.full_name);
}

/**
 * Employer org is a limited company / partnership style entity (show org name first).
 */
export function isEmployerLtdOrPartnershipEntity(user) {
  if (!user) return false;
  const org = user.organization || {};
  const ct = String(org.company_type || user.company_type || '').trim().toUpperCase();
  if (ct === 'LTD' || ct === 'LLP' || ct === 'PLC' || ct === 'PARTNERSHIP') return true;
  if (ct && (ct.includes('LIMITED') || ct.includes('LTD'))) return true;
  const bt = String(org.business_type || user.business_type || '').trim().toLowerCase();
  if (bt.includes('sole trader') || (bt.includes('personal') && !bt.includes('ltd'))) return false;
  if (!ct && bt.includes('business') && (bt.includes('ltd') || bt.includes('partnership'))) return true;
  return false;
}

/**
 * Subcontractor acts as a company (Ltd etc.) vs individual sole trader — aligned with backend subcontractor_display.
 */
export function isSubcontractorBusinessEntity(user) {
  if (!user) return false;
  const org = user.organization || {};
  const ct = String(org.company_type || user.company_type || '').trim().toUpperCase();
  if (ct === 'LTD' || ct === 'LLP' || ct === 'PLC' || ct === 'PARTNERSHIP') return true;
  if (ct && (ct.includes('LIMITED') || ct.includes('LTD'))) return true;
  const bt = String(org.business_type || user.business_type || '').trim().toLowerCase();
  if (bt.includes('personal') || bt.includes('sole')) return false;
  const ut = String(org.user_type || user.user_type || '').trim().toLowerCase();
  if (ut === 'company') return true;
  if (ut === 'individual') return false;
  if (bt.includes('business') && (bt.includes('ltd') || bt.includes('partnership'))) return true;
  return false;
}

function organizationEntityLabel(user) {
  if (!user) return '';
  const org = user.organization || {};
  return (
    norm(org.organization_name) ||
    norm(org.registered_name) ||
    norm(user.registered_name) ||
    norm(org.company_name_sub) ||
    norm(user.company_name_sub) ||
    ''
  );
}

/**
 * Chrome title for employer dashboard: org name for Ltd; registered / personal name for sole trader.
 */
export function employerDashboardOrgTitle(user) {
  if (!user) return '';
  const org = user.organization || {};
  if (isEmployerLtdOrPartnershipEntity(user)) {
    return (
      norm(org.organization_name) ||
      norm(user.registered_name) ||
      soleTraderPersonalLabel(user) ||
      norm(user.full_name) ||
      norm(user.email) ||
      'Organisation'
    );
  }
  return (
    norm(user.registered_name) ||
    soleTraderPersonalLabel(user) ||
    norm(org.organization_name) ||
    norm(user.full_name) ||
    norm(user.email) ||
    'Organisation'
  );
}

/**
 * Chrome title for subcontractor dashboard — mirrors employer_subcontractor_display_name ordering.
 */
export function subcontractorDashboardOrgTitle(user) {
  if (!user) return '';
  const useOrg = isSubcontractorBusinessEntity(user);
  if (useOrg) {
    const label = organizationEntityLabel(user);
    if (label) return label;
    const personal = soleTraderPersonalLabel(user);
    if (personal) return personal;
  } else {
    const personal = soleTraderPersonalLabel(user);
    if (personal) return personal;
    const label = organizationEntityLabel(user);
    if (label) return label;
  }
  return norm(user.full_name) || norm(user.email) || 'Account';
}

/** Main employer account (not a delegated sub-user). */
export function employerIsMainUser(user) {
  if (!user) return false;
  return user.role !== 'bookkeeper' && user.role !== 'manager';
}

export function employerHasPermission(user, permission) {
  if (!user) return false;
  if (employerIsMainUser(user)) return true;
  const permValue = user?.permissions?.[permission];
  return permValue === true || permValue === 'true' || permValue === 1;
}

/**
 * Deep-link: /dashboard/employer?menu=payments — only returns menu if user may access it.
 */
export function parseEmployerDashboardMenuFromSearch(search, user) {
  try {
    const params = new URLSearchParams(search || '');
    let raw = (params.get('menu') || '').toLowerCase().trim();
    if (raw === 'cis_returns') raw = 'cis-returns';
    const allowed = ['subcontractors', 'payments', 'cis-returns'];
    if (!raw || !allowed.includes(raw)) return null;
    if (!user) return null;
    if (raw === 'subcontractors' && !(employerIsMainUser(user) || employerHasPermission(user, 'view_subcontractors'))) {
      return null;
    }
    if (raw === 'payments' && !employerHasPermission(user, 'view_payments')) return null;
    if (raw === 'cis-returns' && !employerIsMainUser(user)) return null;
    if (raw === 'users' && !employerHasPermission(user, 'view_users')) return null;
    return raw;
  } catch {
    return null;
  }
}

